import React, { useState, useRef, useCallback } from 'react'
import styles from './multi-line-styles.module.css'

/**
 * Return type for the Cell renderer function
 */
export interface CellRenderResult {
  /** The content to display in the cell */
  value: React.ReactNode
  /** Optional tooltip text to show on hover */
  tooltip?: string
  /** Optional text color for the cell */
  color?: string
}

/**
 * Column definition for the MultiLineDashboard component.
 */
export interface MultiLineDashboardColumn<TData> {
  /** The header text displayed in the column */
  Header: string
  /** Tooltip text to display on hover (optional) */
  tooltip?: string
  /** Function to access the value from the data row */
  accessor: keyof TData | ((row: TData) => React.ReactNode)
  /** Custom cell renderer that returns value, tooltip, and color (optional) */
  Cell?: (props: { row: TData; value: React.ReactNode }) => CellRenderResult
  /** Text alignment for the column (optional, defaults to 'left') */
  align?: 'left' | 'center' | 'right'
}

interface TooltipState {
  visible: boolean
  text: string
  x: number
  y: number
}

/**
 * Aggregate entry containing a group of data rows with optional subtotals
 */
export interface AggregateEntry<TData> {
  /** Display name for the aggregate group (e.g., technician name, location name) */
  name: string
  /** Unique key for the aggregate */
  key: string
  /** Array of data rows belonging to this aggregate */
  data: TData[]
  /** Optional subtotals for this aggregate group (displayed at the end of the group) */
  subtotals?: React.ReactNode[]
}

/**
 * Props for the MultiLineDashboard component
 */
interface MultiLineDashboardProps<TData> {
  /** Column definitions for the data rows */
  columns: MultiLineDashboardColumn<TData>[]
  /** Array of aggregate entries, each containing grouped data */
  aggregates: AggregateEntry<TData>[]
  /** Header text for the aggregate column (e.g., "Technician", "Location") */
  aggregateColumnHeader: string
  /** Loading state */
  isLoading?: boolean
  /** Custom message when no data is available */
  noDataMessage?: string
  /** Optional grand totals row to display at the bottom */
  totals?: React.ReactNode[]
  /** Optional grand totals row label (defaults to 'Grand Total') */
  totalsLabel?: string
  /** Optional subtotals row label (defaults to 'Subtotal') */
  subtotalsLabel?: string
  /** Optional key extractor for data rows (defaults to index) */
  getRowKey?: (row: TData, aggregateKey: string, index: number) => string | number
  /** Optional max height for the dashboard container */
  maxHeight?: string
  /** Optional callback when a data row is clicked */
  onRowClick?: (row: TData, aggregate: AggregateEntry<TData>) => void
}

function MultiLineDashboardIndicator({
  isLoading,
  noDataMessage,
  colSpan,
}: {
  isLoading: boolean
  noDataMessage: string
  colSpan: number
}) {
  return (
    <tr>
      <td colSpan={colSpan} className={styles.loadingCell}>
        {isLoading ? 'Loading...' : noDataMessage}
      </td>
    </tr>
  )
}

/**
 * A reusable MultiLineDashboard component for displaying grouped/aggregated data
 * in a striped table format where striping alternates between aggregate groups
 * rather than individual rows.
 *
 * @example
 * ```tsx
 * interface DefectData {
 *   typeDisplayName: string
 *   count: number
 *   totalDamageCost: number
 * }
 *
 * interface TechnicianAggregate {
 *   name: string
 *   key: string
 *   data: DefectData[]
 *   subtotals?: React.ReactNode[]
 * }
 *
 * const columns: MultiLineDashboardColumn<DefectData>[] = [
 *   { Header: 'Defect Type', accessor: 'typeDisplayName' },
 *   { Header: 'Count', accessor: 'count' },
 *   { Header: 'Total Damage Cost', accessor: 'totalDamageCost' },
 * ]
 *
 * const aggregates: TechnicianAggregate[] = [
 *   {
 *     name: 'John Smith',
 *     key: 'tech-1',
 *     data: [{ typeDisplayName: 'Scratch', count: 5, totalDamageCost: 100 }],
 *     subtotals: [5, '$100'],
 *   },
 * ]
 *
 * <MultiLineDashboard
 *   columns={columns}
 *   aggregates={aggregates}
 *   totals={[10, '$200']}
 * />
 * ```
 */
function MultiLineDashboard<TData>({
  columns,
  aggregates,
  aggregateColumnHeader,
  isLoading = false,
  noDataMessage = 'No data available.',
  totals,
  totalsLabel = 'Grand Total',
  subtotalsLabel = 'Subtotal',
  getRowKey,
  maxHeight = '75vh',
  onRowClick,
}: MultiLineDashboardProps<TData>) {
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    text: '',
    x: 0,
    y: 0,
  })
  const tooltipRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>, tooltipText: string) => {
      const rect = e.currentTarget.getBoundingClientRect()
      setTooltip({
        visible: true,
        text: tooltipText,
        x: rect.left + rect.width / 2,
        y: rect.top,
      })
    },
    [],
  )

  const handleMouseLeave = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }))
  }, [])

  const getCellValue = (
    row: TData,
    accessor: MultiLineDashboardColumn<TData>['accessor'],
  ): React.ReactNode => {
    if (typeof accessor === 'function') {
      return accessor(row)
    }
    return row[accessor] as React.ReactNode
  }

  const getAlignmentClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center':
        return styles.alignCenter
      case 'right':
        return styles.alignRight
      default:
        return styles.alignLeft
    }
  }

  const hasAnyData = aggregates.some((agg) => agg.data.length > 0)
  const shouldShowIndicator = isLoading || !hasAnyData

  // Total column count = aggregate column + data columns
  const totalColumns = columns.length + 1

  return (
    <div className={styles.dashboardWrapper}>
      {/* Fixed position tooltip rendered outside the scrollable container */}
      {tooltip.visible && (
        <div
          ref={tooltipRef}
          className={styles.tooltip}
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.text}
        </div>
      )}
      <div className={styles.dashboardTableContainer} style={{ maxHeight }}>
        <table className={styles.dashboardTable}>
          <thead>
            <tr>
              {/* Aggregate column header (e.g., "Technician", "Location") */}
              <th className={`${styles.headerCell} ${styles.alignLeft}`}>
                {aggregateColumnHeader}
              </th>
              {columns.map((column, idx) => (
                <th
                  key={idx}
                  className={`${styles.headerCell} ${getAlignmentClass(column.align)} ${
                    column.tooltip ? styles.headerCellWithTooltip : ''
                  }`}
                  onMouseEnter={
                    column.tooltip
                      ? (e) => handleMouseEnter(e, column.tooltip!)
                      : undefined
                  }
                  onMouseLeave={column.tooltip ? handleMouseLeave : undefined}
                >
                  {column.Header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shouldShowIndicator ? (
              <MultiLineDashboardIndicator
                isLoading={isLoading}
                noDataMessage={noDataMessage}
                colSpan={totalColumns}
              />
            ) : (
              <>
                {aggregates.map((aggregate, aggIdx) => {
                  // Skip aggregates with no data
                  if (aggregate.data.length === 0) return null

                  const isOddAggregate = aggIdx % 2 === 0
                  const aggregateColorClass = isOddAggregate
                    ? styles.aggregateOdd
                    : styles.aggregateEven

                  return (
                    <React.Fragment key={aggregate.key}>
                      {/* Data rows for this aggregate */}
                      {aggregate.data.map((row, rowIdx) => {
                        const rowKey = getRowKey
                          ? getRowKey(row, aggregate.key, rowIdx)
                          : `${aggregate.key}-${rowIdx}`
                        const isFirstRowInAggregate = rowIdx === 0
                        const rowSpanCount = aggregate.data.length + (aggregate.subtotals ? 1 : 0)

                        return (
                          <tr
                            key={rowKey}
                            className={`${styles.dataRow} ${aggregateColorClass} ${
                              onRowClick ? styles.clickableRow : ''
                            }`}
                            onClick={
                              onRowClick ? () => onRowClick(row, aggregate) : undefined
                            }
                          >
                            {/* Aggregate name column - only on first row, spans all rows in aggregate */}
                            {isFirstRowInAggregate && (
                              <td
                                rowSpan={rowSpanCount}
                                className={`${styles.aggregateNameCell} ${styles.firstColumnCell}`}
                              >
                                {aggregate.name}
                              </td>
                            )}
                            {columns.map((column, colIdx) => {
                              const rawValue = getCellValue(row, column.accessor)
                              const cellResult = column.Cell
                                ? column.Cell({ row, value: rawValue })
                                : { value: rawValue }
                              const {
                                value: cellContent,
                                tooltip: cellTooltip,
                                color: cellColor,
                              } = cellResult

                              return (
                                <td
                                  key={colIdx}
                                  className={`${styles.dataCell} ${getAlignmentClass(
                                    column.align,
                                  )} ${cellTooltip ? styles.cellWithTooltip : ''}`}
                                  style={cellColor ? { color: cellColor } : undefined}
                                  onMouseEnter={
                                    cellTooltip
                                      ? (e) => handleMouseEnter(e, cellTooltip)
                                      : undefined
                                  }
                                  onMouseLeave={
                                    cellTooltip ? handleMouseLeave : undefined
                                  }
                                >
                                  {cellContent}
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}

                      {/* Subtotals row for this aggregate (if provided) */}
                      {aggregate.subtotals && (
                        <tr className={`${styles.subtotalsRow} ${aggregateColorClass}`}>
                          {/* Aggregate name column is covered by rowSpan, so we render data columns only */}
                          {/* First data column shows the subtotals label */}
                          <td
                            className={`${styles.subtotalsCell} ${styles.subtotalsLabelCell}`}
                          >
                            {subtotalsLabel}
                          </td>
                          {/* Remaining columns show subtotal values */}
                          {aggregate.subtotals.map((subtotalValue, idx) => (
                            <td
                              key={idx}
                              className={`${styles.subtotalsCell} ${getAlignmentClass(
                                columns[idx + 1]?.align,
                              )}`}
                            >
                              {subtotalValue}
                            </td>
                          ))}
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}

                {/* Grand totals row */}
                {totals && (
                  <tr className={styles.totalsRow}>
                    <td className={`${styles.totalsCell} ${styles.firstColumnCell}`}>
                      {totalsLabel}
                    </td>
                    {totals.map((totalValue, idx) => (
                      <td
                        key={idx}
                        className={`${styles.totalsCell} ${getAlignmentClass(
                          columns[idx]?.align,
                        )}`}
                      >
                        {totalValue}
                      </td>
                    ))}
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default MultiLineDashboard
